/**
 * TTL-based cache for derived profile statistics to avoid recomputing on every visit.
 */

export interface ProfileStats {
    totalGroups: number;
    totalTransactions: number;
    totalAccounts: number;
    totalPositions: number;
    totalHoldings: number;
}

export interface ProfileStatsCacheKey {
    username: string;
    displayCurrency: string;
    quoteCurrencyView: string;
    conversionRates: Record<string, number>;
}

const TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CachedProfileStats {
    stats: ProfileStats;
    timestamp: number;
}

const cache = new Map<string, CachedProfileStats>();

function stableConversionRatesKey(rates: Record<string, number>): string {
    const entries = Object.entries(rates).sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify(entries);
}

export function buildProfileStatsCacheKey(params: ProfileStatsCacheKey): string {
    return [
        params.username,
        params.displayCurrency,
        params.quoteCurrencyView,
        stableConversionRatesKey(params.conversionRates),
    ].join("|");
}

export function getCachedProfileStats(params: ProfileStatsCacheKey): ProfileStats | null {
    const key = buildProfileStatsCacheKey(params);
    const entry = cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > TTL_MS) {
        cache.delete(key);
        return null;
    }
    return entry.stats;
}

export function setCachedProfileStats(params: ProfileStatsCacheKey, stats: ProfileStats): void {
    const key = buildProfileStatsCacheKey(params);
    cache.set(key, { stats, timestamp: Date.now() });
}

export function invalidateProfileStatsCache(username?: string): void {
    if (!username) {
        cache.clear();
        return;
    }
    for (const key of cache.keys()) {
        if (key.startsWith(`${username}|`)) {
            cache.delete(key);
        }
    }
}
